
import React, { useState } from 'react';
import { RotateCw, AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { KYPPageState, KYPPageActions } from '../../../lib/kyp-search/hooks/useKYPPageState';
import { CancelSearchDialog } from '../../CancelSearchDialog';

interface KYPSearchIndicatorProps {
  state: KYPPageState;
  actions: KYPPageActions;
  className?: string;
}

export const KYPSearchIndicatorNew: React.FC<KYPSearchIndicatorProps> = ({ 
  state,
  actions,
  className 
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  const {
    phase,
    searchStatus,
    error,
    selectedPlayer,
    playerProfile
  } = state;
  
  const { cancelKYPSearch, startKYPSearch, dismissError } = actions;
  
  // Don't show if not in a search phase or if search is complete/cancelled
  if (
    phase !== 'kyp_running' || 
    searchStatus === 'completed' || 
    searchStatus === 'cancelled'
  ) {
    return null;
  }
  
  // Check if this is a timeout error
  const isTimeoutError = 
    searchStatus === 'failed' && 
    (error?.includes('timed out') || error?.includes('time limit'));
  
  // Get the player name to display in the cancel dialog
  const displayName = playerProfile?.name || selectedPlayer?.name || '';
  
  // Handle retry button click
  const handleRetry = () => {
    dismissError();
    startKYPSearch();
  };
  
  // Render timeout error state
  if (isTimeoutError) {
    return (
      <div className={cn(
        "fixed top-4 right-4 z-50 bg-[#121212] rounded-lg shadow-lg border border-red-600 p-3",
        className
      )}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <span className="text-[#CECFD2] font-medium">
            KYP Search timed out due to a system error.
          </span>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={handleRetry}
            className="text-sm text-blue-400 hover:text-blue-300 font-medium px-3 py-1 rounded-md hover:bg-[#26222E] transition-colors"
          >
            Retry
          </button>
          <button
            onClick={dismissError}
            className="text-sm text-gray-400 hover:text-gray-300 font-medium px-3 py-1 rounded-md hover:bg-[#26222E] transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // Render normal search in progress state
  return (
    <>
      <CancelSearchDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={cancelKYPSearch}
        playerName={displayName}
      />
      <div className={cn(
        "fixed top-4 right-4 z-50 bg-[#121212] rounded-lg shadow-lg border border-[#4E46FF] p-3 flex items-center gap-3",
        className
      )}
      // Add data attributes for compatibility with existing code that may use them
      data-kyp-search-active="true"
      data-kyp-search-id={state.searchId?.toString() || ''}
      data-kyp-search-status={searchStatus || 'in_progress'}
      >
        <div className="flex items-center gap-2">
          <RotateCw className="h-5 w-5 text-[#4E46FF] animate-spin" />
          <span className="text-[#CECFD2] font-medium">
            KYP Search in progress. This can take several minutes.
          </span>
        </div>
        <button
          onClick={() => setShowCancelDialog(true)}
          className="text-sm text-red-400 hover:text-white font-medium px-3 py-1 rounded-md hover:bg-red-700 transition-colors"
        >
          Cancel Search
        </button>
      </div>
    </>
  );
};
